import classNames from "classnames";
import { State } from "redux/store";
import { connect } from "react-redux";
import { MouseEvent, RefObject } from "react";
import { getIsCVATEnabled } from "redux/User/selectors";

import { AnnotationSources, ContactUsComponentType } from "types";

import CVATSelector from "views/pages/DatasetCreationPage/StepsSection/Annotations/AnnotationsDropdown/CVATSelector";

import cvatIcon from "assets/img/CVAT.png";

import styles from "./style.module.scss";
import LabelBoxIcon from "assets/icons/LabelBoxIcon";
import AddAnnotationsIcon from "assets/icons/AddAnnotationsIcon";

interface DropdownOption {
  id: AnnotationSources;
  title: string;
  contactType?: ContactUsComponentType;
}

const SOURCES: DropdownOption[] = [
  { id: AnnotationSources.UPLOAD, title: "Upload Annotations File(s)" },
  { id: AnnotationSources.CVAT, title: "Import from CVAT" },
  { id: AnnotationSources.LABELBOX, title: "Import from Labelbox" },
];

const RenderIcon = ({ src }: { src: string }) => {
  return (
    <img
      src={src}
      width={"17px"}
      height={"17px"}
      alt="annotations-option-icon"
    />
  );
};

const AnnotationsDropdown = ({
  onSkipClick,
  dropdownRef,
  isCVATEnabled,
  handleSelection,
  cvatSelectorRef,
  showCVATSelector,
  handleContactUsClick,
  handleCVATImportClick,
}: AnnotationsDropdownProps) => {
  const renderHeadIcon = (id: AnnotationSources) => {
    switch (id) {
      case AnnotationSources.UPLOAD:
        return (
          <div className={styles.imageIconContainer}>
            <AddAnnotationsIcon />
          </div>
        );

      case AnnotationSources.CVAT:
        return <RenderIcon src={cvatIcon} />;

      case AnnotationSources.LABELBOX:
        return <LabelBoxIcon />;
      default:
        return null;
    }
  };

  const renderRow = (source: DropdownOption, index: number) => {
    const showContactUs = source.id === AnnotationSources.LABELBOX;
    const isCVAT = source.id === AnnotationSources.CVAT;
    if (isCVAT && !isCVATEnabled) {
      return;
    }

    const rowClassNames = classNames(styles.dropdownRow, {
      [styles.relativeRow]: isCVAT,
    });

    return (
      <div
        key={index}
        className={rowClassNames}
        onClick={() => handleSelection(source.id)}
      >
        <div className={styles.rowHeadIcon}>{renderHeadIcon(source.id)}</div>
        <div className={styles.rowTitle}>{source.title}</div>
        {showContactUs && (
          <div
            className={styles.contactUsButton}
            onClick={() => handleContactUsClick(source.contactType!)}
          >
            Contact Us
          </div>
        )}

        {isCVAT && showCVATSelector && (
          <CVATSelector
            selectorRef={cvatSelectorRef}
            onImportClick={handleCVATImportClick}
          />
        )}
      </div>
    );
  };

  return (
    <div
      ref={dropdownRef}
      className={styles.dropdownContainer}
      onClick={(e: MouseEvent<HTMLDivElement>) => e.stopPropagation()}
    >
      <div className={styles.availableOptionsContainer}>
        {SOURCES.map((source: DropdownOption, index: number) => {
          return renderRow(source, index);
        })}
      </div>
      <div className={styles.skipButton} onClick={onSkipClick}>
        SKIP
      </div>
    </div>
  );
};

const mapStateToProps = (state: State) => {
  return {
    isCVATEnabled: getIsCVATEnabled(state),
  };
};

export default connect(mapStateToProps)(AnnotationsDropdown);

interface AnnotationsDropdownProps {
  isCVATEnabled: boolean;
  onSkipClick: () => void;
  showCVATSelector: boolean;
  handleCVATImportClick: () => void;
  dropdownRef: RefObject<HTMLDivElement>;
  cvatSelectorRef: RefObject<HTMLDivElement>;
  handleSelection: (AnnotationSources: AnnotationSources) => void;
  handleContactUsClick: (calledFrom: ContactUsComponentType) => void;
}
